import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  ChatCircleDots, X, PaperPlaneRight, DiscordLogo, EnvelopeSimple,
  Ticket, Sparkle, CircleNotch,
} from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";

const QUICK_REPLIES = [
  "How do I create my first video?",
  "What plan should I pick?",
  "What's an AI Twin?",
  "How does Series Studio work?",
];

const STORAGE_KEY = "tbb_chatbot_session_id";

/**
 * In-app support chat widget.
 * • Floating pink bubble bottom-right on every logged-in page.
 * • Opens a slide-up chat pane with the Lab Assistant (Claude Sonnet 4.6).
 * • Handoff options: Discord community · Email · File a ticket.
 */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffSubject, setHandoffSubject] = useState("");
  const [handoffBody, setHandoffBody] = useState("");
  const [handoffOk, setHandoffOk] = useState(false);
  const bottomRef = useRef(null);

  // Hydrate history on open
  useEffect(() => {
    if (!open || !sessionId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/chatbot/history/${sessionId}`, { headers: getAuthHeaders() });
        setMessages(r.data.messages || []);
      } catch { /* silent */ }
    })();
  }, [open, sessionId]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setSending(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", text: t }]);
    try {
      const r = await axios.post(
        `${API}/chatbot/message`,
        { message: t, session_id: sessionId },
        { headers: getAuthHeaders() },
      );
      if (r.data.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem(STORAGE_KEY, r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Hmm, I couldn't get a response. Please try again in a moment, or ping the team on Discord: https://discord.gg/BmwfRzykqX",
      }]);
    } finally {
      setSending(false);
    }
  };

  const submitTicket = async () => {
    if (!handoffSubject.trim() || !handoffBody.trim()) return;
    try {
      await axios.post(
        `${API}/chatbot/handoff`,
        { session_id: sessionId, subject: handoffSubject, body: handoffBody },
        { headers: getAuthHeaders() },
      );
      setHandoffOk(true);
      setTimeout(() => { setShowHandoff(false); setHandoffOk(false); setHandoffSubject(""); setHandoffBody(""); }, 2200);
    } catch (e) {
      // Fail quiet — the modal already gives Discord/email as backup.
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#E91E8C] text-white shadow-2xl hover:bg-[#c91876] hover:scale-105 transition-all flex items-center justify-center group"
          aria-label="Open support chat"
        >
          <ChatCircleDots size={26} weight="fill" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
          <span className="absolute right-full mr-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
            Ask the Lab
          </span>
        </button>
      )}

      {/* Chat pane */}
      {open && (
        <div
          data-testid="chatbot-pane"
          className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] sm:w-[380px] h-[calc(100vh-6rem)] sm:h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#E91E8C] to-pink-500 text-white p-4 flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                <Sparkle size={18} weight="fill" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm">Lab Assistant · Branding Bar Lab</p>
                <p className="text-[11px] text-white/80 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full" />
                  Online — usually replies in seconds
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white flex-shrink-0"
              data-testid="chatbot-close-btn"
              aria-label="Close chat"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Body */}
          {!showHandoff ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
              {messages.length === 0 && (
                <>
                  <BubbleAssistant text={"Hey! I'm the Lab Assistant. I can help you find the right tool, pick a plan, or troubleshoot. What's up?"} />
                  <div className="pt-1 space-y-1.5">
                    {QUICK_REPLIES.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        disabled={sending}
                        data-testid={`chatbot-quick-${q.slice(0,20).replace(/\W+/g,"-")}`}
                        className="w-full text-left px-3 py-2 rounded-xl bg-white border border-zinc-200 hover:border-[#E91E8C] hover:bg-[#E91E8C]/5 text-xs text-zinc-700 font-medium transition disabled:opacity-50"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {messages.map((m, i) =>
                m.role === "user"
                  ? <BubbleUser key={i} text={m.text} />
                  : <BubbleAssistant key={i} text={m.text} />
              )}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-zinc-500 pl-1">
                  <CircleNotch size={12} className="animate-spin text-[#E91E8C]" />
                  Assistant is typing…
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          ) : (
            <HandoffForm
              handoffOk={handoffOk}
              handoffSubject={handoffSubject}
              handoffBody={handoffBody}
              setHandoffSubject={setHandoffSubject}
              setHandoffBody={setHandoffBody}
              submitTicket={submitTicket}
              onCancel={() => setShowHandoff(false)}
            />
          )}

          {/* Handoff row */}
          {!showHandoff && (
            <div className="px-4 py-2 border-t border-zinc-100 bg-white flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[10px]">
                <a
                  href="https://discord.gg/BmwfRzykqX"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="chatbot-discord-link"
                  className="inline-flex items-center gap-1 text-[#5865F2] hover:underline font-semibold"
                >
                  <DiscordLogo size={12} weight="fill" /> Discord
                </a>
                <a
                  href="mailto:hello@thebrandingbarlab.com"
                  data-testid="chatbot-email-link"
                  className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#E91E8C] font-semibold"
                >
                  <EnvelopeSimple size={12} weight="duotone" /> Email
                </a>
                <button
                  onClick={() => setShowHandoff(true)}
                  data-testid="chatbot-file-ticket-btn"
                  className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#E91E8C] font-semibold"
                >
                  <Ticket size={12} weight="duotone" /> File a ticket
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          {!showHandoff && (
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-zinc-100 bg-white flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question…"
                disabled={sending}
                data-testid="chatbot-input"
                className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C] disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="chatbot-send-btn"
                className="w-10 h-10 rounded-xl bg-[#E91E8C] text-white hover:bg-[#c91876] flex items-center justify-center disabled:opacity-40"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}

function BubbleAssistant({ text }) {
  return (
    <div className="flex items-start gap-2" data-testid="chatbot-bubble-assistant">
      <div className="w-7 h-7 rounded-full bg-[#E91E8C] flex-shrink-0 flex items-center justify-center text-white">
        <Sparkle size={13} weight="fill" />
      </div>
      <div className="max-w-[80%] bg-white border border-zinc-200 rounded-2xl rounded-tl-sm p-2.5 text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function BubbleUser({ text }) {
  return (
    <div className="flex items-start justify-end" data-testid="chatbot-bubble-user">
      <div className="max-w-[80%] bg-[#E91E8C] text-white rounded-2xl rounded-tr-sm p-2.5 text-sm leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function HandoffForm({ handoffOk, handoffSubject, handoffBody, setHandoffSubject, setHandoffBody, submitTicket, onCancel }) {
  if (handoffOk) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center bg-zinc-50" data-testid="chatbot-ticket-success">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
          <Ticket size={24} className="text-emerald-600" weight="fill" />
        </div>
        <p className="font-bold text-zinc-900">Ticket filed!</p>
        <p className="text-sm text-zinc-500 mt-1 max-w-xs">
          Our team will follow up by email. Meanwhile, hop into Discord if you want a faster reply.
        </p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50" data-testid="chatbot-ticket-form">
      <p className="text-xs text-zinc-500">
        We&apos;ll send this straight to the support team. You&apos;ll get a reply at your account email.
      </p>
      <input
        value={handoffSubject}
        onChange={(e) => setHandoffSubject(e.target.value)}
        placeholder="Subject (short summary)"
        data-testid="chatbot-ticket-subject"
        className="w-full px-3 py-2 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C]"
      />
      <textarea
        value={handoffBody}
        onChange={(e) => setHandoffBody(e.target.value)}
        placeholder="What's going on? Include any error messages you saw."
        rows={6}
        data-testid="chatbot-ticket-body"
        className="w-full px-3 py-2 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C] resize-none"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-zinc-200 text-zinc-700 text-sm font-semibold hover:bg-zinc-50"
        >Cancel</button>
        <button
          onClick={submitTicket}
          disabled={!handoffSubject.trim() || !handoffBody.trim()}
          data-testid="chatbot-ticket-submit"
          className="flex-1 py-2 rounded-xl bg-[#E91E8C] text-white text-sm font-semibold hover:bg-[#c91876] disabled:opacity-50"
        >Send ticket</button>
      </div>
    </div>
  );
}
